import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MonitorSmartphone, Activity, BatteryCharging, Users, History,
  Gauge, FileBarChart, Wallet, ArrowRight, Network,
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import { PLATFORM_FEATURES } from "@/lib/siteContent";
import { useContent } from "@/lib/content";
import PageHero from "@/components/site/PageHero";

const EASE = [0.16, 1, 0.3, 1];
const FEATURE_ICONS = [MonitorSmartphone, Activity, BatteryCharging, Users, History, Gauge, FileBarChart, Wallet];

const DashboardMock = () => (
  <div className="rounded-3xl border border-white/15 bg-slate-900/80 backdrop-blur p-5 shadow-[0_32px_64px_rgba(0,0,0,0.45)]">
    <div className="flex items-center gap-1.5 pb-4 border-b border-white/10">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
      <span className="ml-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
        Plataforma HB Charger
      </span>
    </div>
    <div className="grid grid-cols-3 gap-3 pt-4">
      {[
        ["Estações online", "—"],
        ["Sessões hoje", "—"],
        ["Energia (kWh)", "—"],
      ].map(([k, v]) => (
        <div key={k} className="rounded-xl bg-white/5 border border-white/10 p-3.5">
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{k}</p>
          <p className="mt-1 font-display text-xl font-extrabold text-white">{v}</p>
        </div>
      ))}
    </div>
    <div className="mt-3 rounded-xl bg-white/5 border border-white/10 p-4">
      <div className="flex h-20 items-end gap-2">
        {[40, 65, 50, 80, 58, 90, 70, 95, 62, 78].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-amber-500/40 to-amber-400" style={{ height: `${h}%` }} />
        ))}
      </div>
      <p className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-500">
        Consumo por período — demonstração visual
      </p>
    </div>
  </div>
);

export default function PlataformaPage() {
  useSeo(
    "Plataforma de Gerenciamento de Carregadores | OCPP — HB Charger",
    "Gerencie estações de recarga: status em tempo real, sessões, usuários, consumo de energia, relatórios e gestão financeira. Integração compatível com OCPP."
  );

  const { platformCommercial: PLATFORM_COMMERCIAL } = useContent();

  return (
    <>
      <PageHero
        testId="platform-page-title"
        eyebrow="Plataforma de gerenciamento"
        title="Todas as suas estações, em um único painel"
        desc="Sistema de gerenciamento dos carregadores com acompanhamento completo da operação. A plataforma operacional é acessada em ambiente próprio, separado deste site institucional."
      />

      <section className="relative grain overflow-hidden bg-[#0B192C] py-20">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <DashboardMock />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 flex items-center justify-center gap-2 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-400"
          >
            <Network className="h-4 w-4 text-amber-400" />
            Integração compatível com OCPP, conforme o equipamento contratado
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div data-testid="platform-features" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLATFORM_FEATURES.map((f, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <motion.article
                  key={f.titulo}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: EASE }}
                  className="rounded-3xl border border-slate-200/80 bg-white p-7 hover:shadow-[0_20px_40px_rgba(11,25,44,0.08)] transition-shadow duration-500"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0B192C] text-amber-400">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <h2 className="mt-5 font-display text-lg font-semibold tracking-tight text-[#0B192C]">{f.titulo}</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.desc}</p>
                </motion.article>
              );
            })}
          </div>

          <motion.div
            data-testid="platform-commercial"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-3xl border border-amber-300 bg-amber-50 p-8"
          >
            <div>
              <h2 className="font-display text-lg font-bold text-[#0B192C]">Condições comerciais</h2>
              <p className="mt-1 text-sm text-slate-600">{PLATFORM_COMMERCIAL}</p>
            </div>
            <Link
              to="/contato?assunto=plataforma"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#0B192C] px-6 py-3.5 text-sm font-extrabold text-white hover:bg-[#12243d] transition-colors duration-300"
            >
              Solicitar proposta
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
